import {
  Box,
  Table,
  Td,
  Th,
  Thead,
  Tr,
  Divider,
  Flex,
  Icon,
  InputGroup,
  Text,
  Tooltip,
  Tbody,
  IconButton,
} from '@chakra-ui/react';
import { FormikProps } from 'formik';
import _ from 'lodash';
import React, { useState } from 'react';

import {
  utils as commonUtils,
  error as commonError,
} from '@finley-technologies/common';
import { Bank, Report } from '@finley-technologies/finley-types';

import { WipConfig } from '../../types';
import {
  FormFieldLabel,
  InputFormField,
  NumberInputFormField,
} from 'packages/common-frontend/components/FormField';
import { EditIconButton } from 'packages/common-frontend/components/IconButton';
import {
  Select,
  formatSelectOptions,
} from 'packages/common-frontend/components/Select';
import * as utils from 'packages/common-frontend/utils';
import {
  CreditAgreement,
  getBooleanSymbol,
} from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/common';
import { DeleteArrayPropertyButton } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/delete/DeleteArrayPropertyButton';
import { EditPropertyModal } from 'packages/creditAgreements/ui/CreditAgreementFullArtifact/edit/EditPropertyModal';
import { FiChevronDown, FiChevronUp, FiInfo } from 'react-icons/fi';

const isXlsxFinancialCovenantPropertyConfig = (
  financialCovenantPropertyConfig: CreditAgreement.Config.FinancialCovenant.Config,
): financialCovenantPropertyConfig is CreditAgreement.Config.FinancialCovenant.XlsxParseConfig => {
  return (
    financialCovenantPropertyConfig.calculationType ===
    CreditAgreement.Config.FinancialCovenant.CalculateMethod.XlsxParse
  );
};

const configChildren: (
  localFormProps: FormikProps<any>,
) => React.ReactElement = (localFormProps) => {
  const configTypeSelectOptions = formatSelectOptions(
    CreditAgreement.Config.FinancialCovenant.CalculateMethod,
    'sentence',
  );
  const reportTypeSelectOptions = formatSelectOptions(
    Report.ReportTypeIterable,
    'sentence',
  );
  return (
    <>
      <Divider />
      <Flex alignItems="flex-end" width="100%">
        <Text textAlign="center" fontWeight="bold" mr="5px">
          Calculation Config
        </Text>
        <Tooltip
          hasArrow
          placement="right"
          label="This is Finley's configuration to calculate the financial covenants values"
          flexGrow={1}
        >
          <Icon>
            <FiInfo size="24px" />
          </Icon>
        </Tooltip>
      </Flex>
      <Flex alignItems="flex-end" width="100%">
        <FormFieldLabel labelWidth="60" label="Config Type" />
        <InputGroup width="100%">
          <Select
            value={localFormProps.values._config_.calculationType}
            onChange={(val) => {
              localFormProps.setFieldValue('_config_.calculationType', val);
            }}
            options={configTypeSelectOptions}
            width="sm"
          />
        </InputGroup>
      </Flex>
      <Flex alignItems="flex-end" width="100%">
        <FormFieldLabel labelWidth="60" label="Report Type" />
        <InputGroup width="100%">
          <Select
            value={localFormProps.values._config_.reportType}
            onChange={(val) => {
              localFormProps.setFieldValue('_config_.reportType', val);
            }}
            options={reportTypeSelectOptions}
            width="sm"
          />
        </InputGroup>
      </Flex>
      <Flex alignItems="flex-end" width="100%">
        <InputFormField
          propName="_config_.covenantValue.sheet"
          label="Covenant value sheet"
          formProps={localFormProps}
          labelWidth="60"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      </Flex>
      <Flex alignItems="flex-end" width="100%">
        <InputFormField
          propName="_config_.covenantValue.cell"
          label="Covenant value cell"
          formProps={localFormProps}
          labelWidth="60"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      </Flex>
      <Flex alignItems="flex-end" width="100%">
        <InputFormField
          propName="_config_.reportingDate.sheet"
          label="Reporting date sheet"
          formProps={localFormProps}
          labelWidth="60"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      </Flex>
      <Flex alignItems="flex-end" width="100%">
        <InputFormField
          propName="_config_.reportingDate.cell"
          label="Reporting date cell"
          formProps={localFormProps}
          labelWidth="60"
          inputWidth="sm"
          flexDirection="row"
          alignItems="flex-end"
        />
      </Flex>
      <Divider />
    </>
  );
};

const createLimitTd = (
  limit: CreditAgreement.DomainEntity.FinancialCovenantLimit,
  { columnWidth }: { columnWidth: string },
) => {
  switch (limit.valueType) {
    case CreditAgreement.Config.FinancialCovenant.LimitValueType.currency: {
      const currencyCode = limit.currency;
      if (currencyCode && _.keys(Bank.Currency).includes(currencyCode)) {
        return (
          <Td width={columnWidth}>
            {utils.formatCurrencyAmount(limit.value, currencyCode)}
          </Td>
        );
      }
      return <Td width={columnWidth}>{limit.value}</Td>;
    }
    case CreditAgreement.Config.FinancialCovenant.LimitValueType.percentage:
      return (
        <Td width={columnWidth}>
          {utils.numberToPercentageLabel(limit.value)}
        </Td>
      );
    case CreditAgreement.Config.FinancialCovenant.LimitValueType.ratio:
      return <Td width={columnWidth}>{`${limit.value}x`}</Td>;
    default:
      throw new commonError.FinleyError(
        'createLimitTd: Unhandled financial covenant valueType',
      );
  }
};

export const FinancialCovenantRow: React.FC<{
  financialCovenantProperty: CreditAgreement.DomainEntity.FinancialCovenantProperty;
  wipConfig?: WipConfig;
  index: number;
  basePropertyKey: string;
  fullPropertyKey: string;
  columnWidth: string;
}> = ({
  financialCovenantProperty,
  wipConfig,
  index,
  basePropertyKey,
  fullPropertyKey,
  columnWidth,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isConfigOpen, setIsConfigOpen] = useState(false);
  const {
    covenantKey,
    limit,
    limitType,
    section,
    description,
    _note_,
    _isWrittenIn_,
  } = financialCovenantProperty;

  return (
    <>
      <Tr borderTop="1px" borderColor="gray.700">
        <Td width={columnWidth}>{commonUtils.toSentenceCase(covenantKey)}</Td>
        {createLimitTd(limit, { columnWidth })}
        <Td width={columnWidth}>{limitType}</Td>
        <Td width={columnWidth}>{section}</Td>
        <Td w={columnWidth}>{utils.displayValueOrEmpty(description)}</Td>
        {wipConfig && (
          <Td w={columnWidth}>{utils.displayValueOrEmpty(_note_)}</Td>
        )}
        {wipConfig && (
          <Td width={columnWidth}>{getBooleanSymbol(_isWrittenIn_)}</Td>
        )}
        {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
          <Td width={columnWidth}>
            <EditIconButton
              ariaLabel="edit"
              label="Edit"
              onClick={(event) => {
                setIsOpen(true);
                if (event) {
                  event.stopPropagation();
                }
              }}
            />
            <DeleteArrayPropertyButton
              wipConfig={wipConfig}
              basePropertyKey={basePropertyKey}
              idx={index}
            />
            <EditPropertyModal
              isOpen={isOpen}
              setIsOpen={setIsOpen}
              wipConfig={wipConfig}
              basePropertyKey={fullPropertyKey}
              configChildren={configChildren}
            >
              {(localFormProps) => {
                const limitTypeSelectOptions = formatSelectOptions(
                  CreditAgreement.Config.FinancialCovenant.LimitType,
                  'sentence',
                );
                const covenantKeySelectOptions = formatSelectOptions(
                  CreditAgreement.Config.FinancialCovenant.FinancialCovenantKey,
                  'sentence',
                );
                const limitValueTypeSelectOptions = formatSelectOptions(
                  CreditAgreement.Config.FinancialCovenant.LimitValueType,
                  'sentence',
                );
                const currencySelectOptions = formatSelectOptions(
                  Bank.Currency,
                  'upper',
                );
                return (
                  <>
                    <NumberInputFormField
                      propName="limit.value"
                      label="Value"
                      formProps={localFormProps}
                      value={localFormProps.values.limit.value}
                      labelWidth="60"
                      inputWidth="sm"
                      flexDirection="row"
                      alignItems="flex-end"
                      marginTop={5}
                    />
                    <Flex alignItems="flex-end" width="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Covenant Property"
                      />
                      <InputGroup width="100%">
                        <Select
                          value={localFormProps.values.covenantKey}
                          onChange={(val) => {
                            localFormProps.setFieldValue('covenantKey', val);
                            localFormProps.setFieldValue(
                              'value',
                              _.startCase(val),
                            );
                          }}
                          options={covenantKeySelectOptions}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex alignItems="flex-end" width="100%">
                      <FormFieldLabel labelWidth="60" label="Limit Type" />
                      <InputGroup width="100%">
                        <Select
                          value={localFormProps.values.limitType}
                          onChange={(val) => {
                            localFormProps.setFieldValue('limitType', val);
                          }}
                          options={limitTypeSelectOptions}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    <Flex alignItems="flex-end" width="100%">
                      <FormFieldLabel
                        labelWidth="60"
                        label="Limit value Type"
                      />
                      <InputGroup width="100%">
                        <Select
                          value={localFormProps.values.limit.valueType}
                          onChange={(val) => {
                            localFormProps.setFieldValue(
                              'limit.valueType',
                              val,
                            );
                          }}
                          options={limitValueTypeSelectOptions}
                          width="sm"
                        />
                      </InputGroup>
                    </Flex>
                    {localFormProps.values.limit.valueType === 'currency' && (
                      <Flex alignItems="flex-end" width="100%">
                        <FormFieldLabel labelWidth="60" label="Currency" />
                        <InputGroup width="100%">
                          <Select
                            value={localFormProps.values.limit.currency}
                            onChange={(val) => {
                              localFormProps.setFieldValue(
                                'limit.currency',
                                val,
                              );
                            }}
                            options={currencySelectOptions}
                            width="sm"
                          />
                        </InputGroup>
                      </Flex>
                    )}
                  </>
                );
              }}
            </EditPropertyModal>
          </Td>
        ) : (
          <Td />
        )}
        {wipConfig?.isMutateAllowed(fullPropertyKey) ? (
          <Td width={columnWidth}>
            {isConfigOpen ? (
              <IconButton
                aria-label="close config"
                icon={<FiChevronUp />}
                onClick={() => setIsConfigOpen(false)}
                variant="ghost"
              />
            ) : (
              <IconButton
                aria-label="display config"
                icon={<FiChevronDown />}
                onClick={() => setIsConfigOpen(true)}
                variant="ghost"
              />
            )}
          </Td>
        ) : (
          <Td />
        )}
      </Tr>
      {isConfigOpen &&
        financialCovenantProperty._config_ &&
        isXlsxFinancialCovenantPropertyConfig(
          financialCovenantProperty._config_,
        ) && (
          <Box ml="35px" mt="35px" width="100%">
            <Text mb="10px" textAlign="left" fontWeight="bold" fontSize="24px">
              Configs
            </Text>
            <Table>
              <Thead>
                <Tr>
                  <Th>Report type</Th>
                  <Th>Reporting date sheet</Th>
                  <Th>Reporting date cell</Th>
                  <Th>Covenant value sheet</Th>
                  <Th>Covenant value cell</Th>
                </Tr>
              </Thead>
              <Tbody>
                <Tr borderBottom="none">
                  <Td width="fit-content">
                    {(financialCovenantProperty._config_ &&
                      financialCovenantProperty._config_.reportType) ||
                      '-'}
                  </Td>
                  <Td width="fit-content">
                    {(financialCovenantProperty._config_ &&
                      financialCovenantProperty._config_.reportingDate.sheet) ||
                      '-'}
                  </Td>
                  <Td width="fit-content">
                    {(financialCovenantProperty._config_ &&
                      financialCovenantProperty._config_.reportingDate.cell) ||
                      '-'}
                  </Td>
                  <Td width="fit-content">
                    {(financialCovenantProperty._config_ &&
                      financialCovenantProperty._config_.covenantValue.sheet) ||
                      '-'}
                  </Td>
                  <Td width="fit-content">
                    {(financialCovenantProperty._config_ &&
                      financialCovenantProperty._config_.covenantValue.cell) ||
                      '-'}
                  </Td>
                </Tr>
              </Tbody>
            </Table>
          </Box>
        )}
    </>
  );
};

export const FinancialCovenantTable: React.FC<{
  sanitizedFinancialCovenants: CreditAgreement.DomainEntity.FinancialCovenantProperty[];
  wipConfig?: WipConfig;
  basePropertyKey: string;
}> = ({ sanitizedFinancialCovenants, wipConfig, basePropertyKey }) => {
  if (
    !sanitizedFinancialCovenants ||
    _.isEmpty(sanitizedFinancialCovenants) ||
    !Object.keys(sanitizedFinancialCovenants).length ||
    !Object.values(sanitizedFinancialCovenants).length
  ) {
    return <Text>There are currently no properties in this section.</Text>;
  }

  const _financialCovenantTableHeaders = [
    'Property',
    'Value',
    'Limit Type',
    'Section',
    'Description',
    'Note',
    'Is Written in',
  ];

  const financialCovenantTableHeaders = wipConfig
    ? _financialCovenantTableHeaders
    : _financialCovenantTableHeaders.filter(
        (header) => !_.includes(['Note', 'Is Written in'], header),
      );
  const columnWidth = `${Math.floor(
    95 / (financialCovenantTableHeaders.length + 1),
  )}%`;

  if (wipConfig) {
    financialCovenantTableHeaders.push('Action');
  }
  return (
    <>
      <Table style={{ tableLayout: 'fixed' }} borderWidth="1px">
        <Thead>
          <Tr>
            {_.map(financialCovenantTableHeaders, (header) => {
              return (
                <Th width={columnWidth} key={`th-${header}`}>
                  {header}
                </Th>
              );
            })}
            {wipConfig && <Th width="5%" />}
          </Tr>
        </Thead>
        <Tbody>
          {_.map(
            _.toPairs(sanitizedFinancialCovenants),
            ([propertyKey, financialCovenantProperty], index) => {
              if (!financialCovenantProperty) {
                return null;
              }
              const fullPropertyKey = `${basePropertyKey}.${propertyKey}`;
              return (
                <FinancialCovenantRow
                  key={`row--${index}`}
                  financialCovenantProperty={financialCovenantProperty}
                  index={index}
                  wipConfig={wipConfig}
                  basePropertyKey={basePropertyKey}
                  fullPropertyKey={fullPropertyKey}
                  columnWidth={columnWidth}
                />
              );
            },
          )}
        </Tbody>
      </Table>
    </>
  );
};
